import { keepPreviousData as keepData } from "@tanstack/react-query";
import { useAuthQuery } from "@rentablo/api";
import {
  CashFlowAndPerformanceStatisticsList,
  performanceByDepots,
} from "@rentablo/api/apiv1";
import { ACCOUNT_PERFORMANCE_BY_ASSET_TYPES } from "@api/cacheKeys";
import { sameDateInUTC } from "@helpers";
import {
  ASSET_CLASSES,
  ASSET_CLASS_INVESTMENT_TYPES,
  AssetClassInvestmentType,
} from "./assetClasses";

export interface Attrs {
  accountIds?: number[];
  startDate?: Date;
  endDate?: Date;
  interval?: "day" | "week" | "month" | "year";
  keepPreviousData?: boolean;
  displayCurrency?: string;
  // Requests only the total of these investment types, as one entry, instead
  // of the total plus one entry per asset class. Empty disables the request.
  combinedAssetClasses?: AssetClassInvestmentType[];
}

export const performanceParamsList = (
  combinedAssetClasses?: AssetClassInvestmentType[]
) =>
  combinedAssetClasses
    ? [
        {
          assetClasses: combinedAssetClasses,
          includeCash: false,
          includeDividends: true,
        },
      ]
    : [
        { assetClasses: [], includeCash: false, includeDividends: true },
        ...ASSET_CLASSES.map((assetClass) => ({
          assetClasses: [...ASSET_CLASS_INVESTMENT_TYPES[assetClass]],
          includeCash: false,
          includeDividends: false,
        })),
      ];

export const usePerformanceByAssetClass = (attrs: Attrs) => {
  const {
    accountIds,
    startDate,
    endDate,
    interval,
    keepPreviousData = true,
    displayCurrency,
    combinedAssetClasses,
  } = attrs;

  const query = useAuthQuery<
    CashFlowAndPerformanceStatisticsList,
    AxiosApiError,
    CashFlowAndPerformanceStatisticsList
  >(
    [ACCOUNT_PERFORMANCE_BY_ASSET_TYPES, attrs],
    ({ token }) =>
      performanceByDepots(token, {
        createCashFlowAndPerformanceStatisticsParams: {
          accountIds,
          startDate: startDate
            ? sameDateInUTC(startDate).toISOString()
            : undefined,
          endDate: endDate ? sameDateInUTC(endDate).toISOString() : undefined,
          interval,
          displayCurrency,
          cashFlowAndPerformanceStatisticsParamsList:
            performanceParamsList(combinedAssetClasses),
        },
      }),
    {
      enabled:
        !!accountIds &&
        accountIds.length > 0 &&
        (!combinedAssetClasses || combinedAssetClasses.length > 0),
      placeholderData: keepPreviousData ? keepData : undefined,
    }
  );

  const list = query.data?.cashFlowAndPerformanceStatisticsList ?? [];
  // In request order: the total, then ASSET_CLASSES.
  const [
    total,
    allHoldings,
    funds,
    etfs,
    derivative,
    bonds,
    crypto,
    currencies,
    preciousMetals,
  ] = list;

  return {
    ...query,
    total,
    allHoldings,
    funds,
    etfs,
    derivative,
    bonds,
    crypto,
    currencies,
    preciousMetals,
    responseCurrency: query.data?.displayCurrency,
  };
};
